import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { createMutationHook } from '@/hooks/lib/createMutationHook';
import { apiPatch, apiPost, apiDelete, operationsClient } from '@/lib/api';
import { estimateDocTemplateEndpoints } from '@/lib/api/endpoints';
import { estimateDocTemplateKeys } from '@/lib/queryKeys';
import type {
  CreateEstimateDocTemplateDto,
  EstimateDocTemplate,
  UpdateEstimateDocTemplateDto,
} from '@/types/estimateDocTemplate';

const invalidateLists = (queryClient: QueryClient) =>
  void queryClient.invalidateQueries({ queryKey: estimateDocTemplateKeys.lists() });

/**
 * Refetch every estimate's SERVER-resolved document layout. Editing, publishing,
 * (un)defaulting or deleting a design changes what estimates that resolve through
 * it — or through the tenant default — render, and those `document-layout`
 * queries are cached under their own per-estimate key, so they must be dropped or
 * the preview/download keeps the pre-change layout until it goes stale.
 */
const invalidateResolvedLayouts = (queryClient: QueryClient) =>
  void queryClient.invalidateQueries({
    predicate: (q) => q.queryKey.includes('document-layout'),
  });

export const useCreateEstimateDocTemplate = createMutationHook<
  EstimateDocTemplate,
  CreateEstimateDocTemplateDto
>({
  mutationFn: (dto) =>
    apiPost<EstimateDocTemplate, CreateEstimateDocTemplateDto>(
      operationsClient,
      estimateDocTemplateEndpoints.create(),
      dto,
    ),
  onSuccess: (_data, _vars, queryClient) => {
    invalidateLists(queryClient);
    toast.success('Design created');
  },
});

export const useUpdateEstimateDocTemplate = createMutationHook<
  EstimateDocTemplate,
  { id: string; dto: UpdateEstimateDocTemplateDto }
>({
  mutationFn: ({ id, dto }) =>
    apiPatch<EstimateDocTemplate, UpdateEstimateDocTemplateDto>(
      operationsClient,
      estimateDocTemplateEndpoints.update(id),
      dto,
    ),
  onSuccess: (_data, { id }, queryClient) => {
    invalidateLists(queryClient);
    void queryClient.invalidateQueries({ queryKey: estimateDocTemplateKeys.detail(id) });
    invalidateResolvedLayouts(queryClient);
    toast.success('Design saved');
  },
});

export const usePublishEstimateDocTemplate = createMutationHook<
  EstimateDocTemplate,
  { id: string }
>({
  mutationFn: ({ id }) =>
    apiPost<EstimateDocTemplate, undefined>(
      operationsClient,
      estimateDocTemplateEndpoints.publish(id),
    ),
  onSuccess: (_data, { id }, queryClient) => {
    invalidateLists(queryClient);
    void queryClient.invalidateQueries({ queryKey: estimateDocTemplateKeys.detail(id) });
    invalidateResolvedLayouts(queryClient);
    toast.success('Design published');
  },
});

export const useCloneEstimateDocTemplate = createMutationHook<EstimateDocTemplate, { id: string }>({
  mutationFn: ({ id }) =>
    apiPost<EstimateDocTemplate, undefined>(
      operationsClient,
      estimateDocTemplateEndpoints.clone(id),
    ),
  onSuccess: (_data, _vars, queryClient) => {
    invalidateLists(queryClient);
    toast.success('Design duplicated');
  },
});

export const useSetDefaultEstimateDocTemplate = createMutationHook<
  EstimateDocTemplate,
  { id: string }
>({
  mutationFn: ({ id }) =>
    apiPost<EstimateDocTemplate, undefined>(
      operationsClient,
      estimateDocTemplateEndpoints.setDefault(id),
    ),
  onSuccess: (_data, _vars, queryClient) => {
    invalidateLists(queryClient);
    invalidateResolvedLayouts(queryClient);
    toast.success('Default design updated');
  },
});

export const useDeleteEstimateDocTemplate = createMutationHook<{ deleted: true }, { id: string }>({
  mutationFn: ({ id }) =>
    apiDelete<{ deleted: true }>(operationsClient, estimateDocTemplateEndpoints.delete(id)),
  onSuccess: (_data, _vars, queryClient) => {
    invalidateLists(queryClient);
    invalidateResolvedLayouts(queryClient);
    toast.success('Design deleted');
  },
});
